"use client";
import React, { useState } from "react";
import { Send, Phone, Mail, User, MessageSquare, Tag } from "lucide-react";
import SubscribeService from "@/app/services/subscribe";
import { useTranslations } from 'next-intl';

export default function ContactForm() {
    const t = useTranslations("General");
    const [status, setStatus] = useState<"" | "loading" | "success" | "error">("");
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsLoading(true)
        const form = e.currentTarget;
        const formData = new FormData(form);
        const data = Object.fromEntries(formData.entries());
        try {
            const result: any = await SubscribeService.sendMessage(data);
            if (result?.data?.success) {
                form.reset()
                setStatus("success")
            } else {
                setStatus(result?.data?.message ?? t('message_sent_error'))
            }
        } catch (error: any) {
            setStatus(error?.response?.data?.message ?? t('message_sent_error'))
        } finally {
            setIsLoading(false)
        }
    };

    return (
        <div className="text-center">
            <div className='my-5 p-10 mb-10 bg-gradient-primary items-center max-w-4xl mx-auto grid md:grid-cols-1 gap-8'>
                <form onSubmit={handleSubmit} className="space-y-6 w-full">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Name */}
                        <div className="space-y-2">
                            <label htmlFor="name" className="text-sm font-semibold text-gray-700 ml-1">{t('name')}</label>
                            <div className="relative">
                                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    required
                                    id="name"
                                    name="name"
                                    type="text"
                                    placeholder=""
                                    className="w-full pl-12 pr-4 py-4 bg-gray-50 border-transparent border focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-500/10 transition-all outline-none"
                                />
                            </div>
                        </div>

                        {/* Email */}
                        <div className="space-y-2">
                            <label htmlFor="email" className="text-sm font-semibold text-gray-700 ml-1">{t('email')}</label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    required
                                    id="email"
                                    name="email"
                                    type="email"
                                    placeholder=""
                                    className="w-full pl-12 pr-4 py-4 bg-gray-50 border-transparent border focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-500/10 transition-all outline-none"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Phone */}
                        <div className="space-y-2">
                            <label htmlFor="phone" className="text-sm font-semibold text-gray-700 ml-1">{t('phone')}</label>
                            <div className="relative">
                                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    id="phone"
                                    name="phone"
                                    type="tel"
                                    placeholder=""
                                    className="w-full pl-12 pr-4 py-4 bg-gray-50 border-transparent border focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-500/10 transition-all outline-none"
                                />
                            </div>
                        </div>

                        {/* Subject */}
                        <div className="space-y-2">
                            <label htmlFor="subject" className="text-sm font-semibold text-gray-700 ml-1">{t('subject')}</label>
                            <div className="relative">
                                <Tag className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                <input
                                    required
                                    id="subject"
                                    name="subject"
                                    type="text"
                                    placeholder=""
                                    className="w-full pl-12 pr-4 py-4 bg-gray-50 border-transparent border focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-500/10 transition-all outline-none"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Message */}
                    <div className="space-y-2">
                        <label htmlFor="message" className="text-sm font-semibold text-gray-700 ml-1">{t('message')}</label>
                        <div className="relative">
                            <MessageSquare className="absolute left-4 top-6 text-gray-400" size={18} />
                            <textarea
                                required
                                id="message"
                                name="message"
                                rows={5}
                                placeholder=""
                                className="w-full pl-12 pr-4 py-4 bg-gray-50 border-transparent border focus:border-violet-500 focus:bg-white focus:ring-4 focus:ring-violet-500/10 transition-all outline-none resize-none"
                            />
                        </div>
                    </div>

                    {/* Submit Button */}
                    <button
                        disabled={isLoading}
                        type="submit"
                        className="w-full flex items-center justify-center gap-3 py-4 bg-violet-600 text-white font-bold hover:bg-violet-700 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? (
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <>
                                {t('send_message')}
                                <Send size={18} />
                            </>
                        )}
                    </button>

                    {/* Success Message */}
                    {status === "success" ? (
                        <p className="text-center text-green-600 font-medium animate">
                            Message sent successfully! We'll get back to you soon.
                        </p>
                    ) : (
                        <p className="text-center text-red-600 font-medium">
                           {status}
                        </p>
                    )}
                </form>
            </div>
        </div>
    );
}